/**
 * Sale event hub `/{cc}/{lang}/sale/:event` (web plan W0.15 / W3.4), born at its
 * final URL: `/pk/en/sale/11-11`, `/pk/en/sale/blessed-friday`, … It keeps that
 * URL through W1–W4 and is reused every year (the year lives in the title).
 *
 * W3.4: data switches from the static `@/seo/saleEvents` config to the backend
 * `salePage` query (B3.7a). The static config is still used for fast 404
 * detection (unpublished / unknown slugs) and as a fallback while the backend
 * response is in-flight. Once backend data arrives:
 *   - The year-switch logic updates the title year (see saleEventDisplayWindow).
 *   - livePromotions + upcomingPromotions from salePage replace the old
 *     paginated publishedPromotions approach.
 *   - previousParticipants replaces the static lastYearParticipants list.
 *
 * MW8: the country's sale pages are data (`locales { hasSaleEvents }`,
 * @/hooks/useSaleCountries), and so are its events' slugs: the static config is
 * Pakistan's calendar only, so another country's event (or a Pakistan one the
 * Dashboard added) is found by `salePage` alone (`SaleEventDataPage`).
 *
 * An unknown or unpublished event is the 404 page (noindex).
 */
import { lazy, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Tag } from 'lucide-react';
import { PromotionCard } from '@/components/cards';
import { LastYearBrands } from '@/components/sale/LastYearBrands';
import { SaleCountdown } from '@/components/sale/SaleCountdown';
import { SaleEventCard } from '@/components/sale/SaleEventCard';
import { SaleFollowCta } from '@/components/sale/SaleFollowCta';
import { Button, CardSkeleton, ErrorState, SectionHeader } from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { useNow } from '@/hooks/useNow';
import { useSaleCountry } from '@/hooks/useSaleCountries';
import {
  useSaleEventPromotions,
  useSaleEventsList,
  useSalePage,
  type SaleOffersState,
  type SalePageData,
} from '@/hooks/useSaleEvent';
import { PageHead } from '@/components/seo/PageHead';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { DEFAULT_LOCALE, localeKey, type Locale } from '@/seo/locales';
import {
  mapBackendParticipants,
  mapBackendSaleEventRows,
  mapBackendToSaleEvent,
  publishedSaleEvents,
  saleCalendarPath,
  saleEventBySlug,
  saleEventPath,
  type SaleEvent,
} from '@/seo/saleEvents';
import { saleEventPageHead } from '@/seo/pageHeads';
import { entityAlternates, saleEventDates } from '@/seo/templates';

/** An event slug as the backend allows it. */
const SLUG = /^[a-z0-9][a-z0-9-]*$/;

// Lazy like every page in App.tsx, so the 404 page stays in its own chunk.
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/** The owner's explainer as paragraphs (blank lines separate them). */
export function explainerParagraphs(text: string | null | undefined): string[] {
  return (text ?? '')
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function OffersEmpty({ name }: { name: string }) {
  return (
    <div className="mt-4 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line px-6 py-12 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
        <Tag size={28} className="text-primary" aria-hidden="true" />
      </span>
      <p className="font-bold text-ink">Offers for {name} will appear here</p>
    </div>
  );
}

/**
 * The event's tagged, published promotions (upcoming and active), a page at a
 * time. The page loads them (the JSON-LD lists the same offers).
 */
function SaleOffers({ event, offers }: { event: SaleEvent; offers: SaleOffersState }) {
  const { items, loading, error } = offers;

  let body: ReactNode;
  if (!offers.enabled || (items.length === 0 && !loading && !error)) {
    body = <OffersEmpty name={event.name} />;
  } else if (items.length === 0 && loading) {
    body = (
      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  } else if (items.length === 0 && error) {
    body = (
      <ErrorState
        title="Couldn't load the offers"
        message={error}
        onRetry={offers.refetch}
        className="py-10"
      />
    );
  } else {
    body = (
      <>
        <ul className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {items.map((promotion) => (
            // The <li> is the grid item and stretches to its row; `*:h-full`
            // passes that height on to the card, so a row's cards are equal.
            <li key={promotion.id} className="h-full *:h-full">
              <PromotionCard promotion={promotion} variant="grid" />
            </li>
          ))}
        </ul>
        {error && !loading && (
          <div className="mt-6 flex items-center justify-center gap-2 text-sm">
            <span className="text-ink-secondary">Couldn&apos;t load more</span>
            <button
              type="button"
              onClick={offers.refetch}
              className="font-semibold text-primary-strong hover:underline"
            >
              Retry
            </button>
          </div>
        )}
        {offers.hasMore && !error && (
          <div className="mt-6 flex justify-center">
            <Button variant="outline" size="sm" loading={loading} onClick={offers.loadMore}>
              Show more offers
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <section aria-labelledby="sale-offers-heading">
      <h2 id="sale-offers-heading" className="text-xl font-extrabold tracking-tight text-ink">
        {event.name} offers
        {typeof offers.totalRecords === 'number' && offers.totalRecords > 0 && (
          <span className="text-base font-semibold text-ink-tertiary">{` (${offers.totalRecords})`}</span>
        )}
      </h2>
      {body}
    </section>
  );
}

export interface SaleEventContentProps {
  event: SaleEvent;
  locale?: Locale;
  /** Backend salePage data; when provided, replaces static offers and participants. */
  pageData?: SalePageData | null;
  /**
   * The other events of the country, for "More sales" (the backend's list). Left
   * out, the static config's other events (Pakistan's calendar) are shown.
   */
  moreEvents?: readonly SaleEvent[];
}

/** The hub for one (published) event. Exported so tests can render any config. */
export function SaleEventContent({ event, locale = DEFAULT_LOCALE, pageData, moreEvents }: SaleEventContentProps) {
  // W3.4: when backend page data is available, use its promotions directly
  // (no pagination needed); fall back to the legacy tag-filtered query otherwise.
  const legacyTagId = pageData ? null : event.tagId;
  const legacyOffers = useSaleEventPromotions(legacyTagId);

  // Build a static SaleOffersState from backend livePromotions + upcomingPromotions.
  const backendOffers = useMemo<SaleOffersState | null>(() => {
    if (!pageData) return null;
    const items = [...pageData.livePromotions, ...pageData.upcomingPromotions];
    return {
      enabled: true,
      items,
      loading: false,
      error: null,
      totalRecords: items.length,
      hasMore: false,
      loadMore: () => {},
      refetch: () => {},
    };
  }, [pageData]);

  const offers = backendOffers ?? legacyOffers;
  const { items: offerItems, totalRecords: offerCount } = offers;

  // Use backend previousParticipants when available; fall back to static config.
  const participants = useMemo(
    () => (pageData ? mapBackendParticipants(pageData.previousParticipants) : event.lastYearParticipants),
    [pageData, event.lastYearParticipants],
  );
  // JSON-LD (web plan W0.7): a CollectionPage whose ItemList is the offers
  // on screen, each named by its H1 ("{Brand}: {headline}").
  // The SEO server builds the same head from the same query (web plan W1.3).
  const t = useTranslate();
  // The hreflang pairs of the same event in another country (backend `seo.alternates`).
  const alternates = useMemo(() => entityAlternates(pageData?.seo), [pageData]);
  const head = useMemo(
    () => saleEventPageHead(event, { items: offerItems, totalRecords: offerCount }, locale, { t, alternates }),
    [event, locale, offerItems, offerCount, t, alternates],
  );
  const now = useNow(60_000);
  const paragraphs = explainerParagraphs(event.explainer);
  const others = (moreEvents ?? publishedSaleEvents()).filter((e) => e.slug !== event.slug);
  const singleDay = event.startDate === event.endDate;
  const dates = saleEventDates(event, { locale, t });

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead head={head} />
      <Breadcrumbs crumbs={head.breadcrumbs} className="mb-4" />

      <header className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold tracking-tight text-ink md:text-3xl">{head.h1}</h1>
          <p className="mt-2 text-sm font-semibold text-ink-secondary">
            {singleDay ? <time dateTime={event.startDate}>{dates}</time> : dates}
          </p>
        </div>
        <SaleCountdown event={event} locale={locale} />
      </header>

      {paragraphs.length > 0 && (
        <div className="mt-6 max-w-3xl space-y-3 text-ink-secondary">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      )}

      <div className="mt-10 space-y-12">
        <SaleOffers key={event.slug} event={event} offers={offers} />
        <LastYearBrands participants={participants} />
        <SaleFollowCta />
        {others.length > 0 && (
          <section aria-label="More sales">
            <SectionHeader title="More sales" to={saleCalendarPath(locale)} seeAllLabel="Sale calendar" />
            <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((other) => (
                <li key={other.slug}>
                  <SaleEventCard event={other} now={now} locale={locale} />
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

export interface SaleEventPageProps {
  /** The route's locale: routes are generated per locale with literal segments (App.tsx). */
  locale?: Locale;
}

/**
 * W3.4: fetches `salePage` from the backend and overlays it onto the static
 * event config. Declared at module level (not inside `SaleEventPage`) so
 * React Refresh does not treat the entry as a boundary.
 */
function SaleEventPageLoader({ event, locale }: { event: SaleEvent; locale: Locale }) {
  const { data: pageData } = useSalePage(event.slug, localeKey(locale));
  // Once backend data arrives, remap the event shape (year-switch logic, explainer).
  const enrichedEvent = useMemo(
    () => (pageData ? (mapBackendToSaleEvent(pageData.event, pageData) ?? event) : event),
    [pageData, event],
  );
  return <SaleEventContent key={event.slug} event={enrichedEvent} locale={locale} pageData={pageData} />;
}

/**
 * An event the static config does not know (another country's, or one the
 * Dashboard added): everything comes from `salePage` and the country's list.
 * Declared at module level like the loader above (React Refresh).
 */
function SaleEventDataPage({ slug, locale }: { slug: string; locale: Locale }) {
  const { data: pageData, loading } = useSalePage(slug, localeKey(locale));
  const { events: listed } = useSaleEventsList(locale);
  const now = useNow(60_000);
  const event = useMemo(() => (pageData ? mapBackendToSaleEvent(pageData.event, pageData, now) : null), [pageData, now]);
  const moreEvents = useMemo(
    () => mapBackendSaleEventRows(listed ?? [], now),
    [listed, now],
  );
  if (!event) return loading ? <div aria-busy="true" className="min-h-[50vh]" /> : <NotFoundPage />;
  return <SaleEventContent key={slug} event={event} locale={locale} pageData={pageData} moreEvents={moreEvents} />;
}

export default function SaleEventPage({ locale = DEFAULT_LOCALE }: SaleEventPageProps) {
  const { event: param = '' } = useParams();
  const { pathname, search, hash } = useLocation();
  const saleCountry = useSaleCountry(locale);
  const slug = param.toLowerCase();
  // The static config is Pakistan's calendar: another country never reads it.
  const event = locale.cc === DEFAULT_LOCALE.cc ? saleEventBySlug(slug) : null;

  // A country without published sale events has no sale pages (src/seo/saleCountries.ts);
  // one whose flag is still on its way waits instead of flashing the 404 page.
  if (!saleCountry.enabled) return saleCountry.ready ? <NotFoundPage /> : <div aria-busy="true" className="min-h-[50vh]" />;

  // `/PK/EN/sale/Blessed-Friday` or a trailing slash: the page itself, at its
  // one lowercase URL (the W1 server does this as a 301).
  // A slug is lowercase letters, digits and dashes (backend INVALID_SLUG): anything else is no event.
  if (!SLUG.test(slug)) return <NotFoundPage />;
  const path = saleEventPath({ slug }, locale);
  if (pathname !== path) return <Navigate replace to={{ pathname: path, search, hash }} />;

  return event ? <SaleEventPageLoader event={event} locale={locale} /> : <SaleEventDataPage slug={slug} locale={locale} />;
}
